import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ConfigColumn, TooltipPositionType, TypeColumn } from '@eqproject/eqp-table';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizationDTO } from '../../../models/authorization.model';
import { CompanyListDTO } from '../../../models/company.model';
import { UserListDTO } from '../../../models/user.model';
import { AuthService } from '../../../services/auth.service';
import { DialogService } from '../../../services/dialog.service';
import { GlobalService } from '../../../services/global.service';
import { UserImpersonationsService } from '../../../services/user-impersonations.service';

@Component({
  selector: 'app-manage-authorizations',
  templateUrl: './manage-authorizations.component.html',
  styleUrls: ['./manage-authorizations.component.scss']
})
export class ManageAuthorizationsComponent implements OnInit {

  //#region AUTORIZZAZIONI CONCESSE
  //Lista companies a cui la company connessa ha concesso l'autorizzazione
  listCompaniesGranted : CompanyListDTO[] = [];
  usersGranted : UserListDTO[] = [];
  grantedColumns : ConfigColumn[] = [];
  showCompaniesGranted : boolean = true;
  nameGrantedCompanyToShow : string;
  //#endregion

  //#region AUTORIZZAZIONI OTTENUTE
  //Lista companies che hanno concesso l'autorizzazione alla company connessa
  listCompaniesGranting : CompanyListDTO[] = [];
  usersGranting : UserListDTO[] = [];
  grantingColumns : ConfigColumn[] = [];
  showCompaniesGranting : boolean = true;
  nameGrantingCompanyToShow : string;
  //#endregion

  @ViewChild("modalAddAuthorization", {static: true}) modalAddAuthorization : TemplateRef<any>;
  modalAddAuthorizationRef : MatDialogRef<TemplateRef<any>>;
  authEmail : string;
  authForm : UntypedFormGroup;

  constructor(public translate : TranslateService,
              private userImpersonationService : UserImpersonationsService,
              private authService : AuthService,
              private dialog : MatDialog,
              private formBuilder : UntypedFormBuilder) { }

  ngOnInit(): void {
    this.configureColumns();
    this.loadCompanies();
  }

  configureColumns(){
    this.configureGrantedColumns();
    this.configureGrantingColumns();
  }

  configureGrantedColumns(forCompanies : boolean = true){
    if(forCompanies){
      this.grantedColumns = [
        { key: "Denomination", display: this.translate.instant("GENERIC_FIELDS.DENOMINATION"), styles: {flex: "0 0 30%"} },
        { key: "Name", display: this.translate.instant("GENERIC_FIELDS.NAME"), styles: {flex: "0 0 20%"}  },
        { key: "Surname", display: this.translate.instant("GENERIC_FIELDS.SURNAME"), styles: {flex: "0 0 20%"}  },
        { key: "VatNumber", display: this.translate.instant("GENERIC_FIELDS.VAT"), styles: {flex: "0 0 20%"}  },
        {
          key: "action",
          display: "",
          type: TypeColumn.SimpleAction,
          styles: {flex: "0 0 10%"},
          actions: [
            {
              icon: "zoom_in",
              name: "",
              fn : (element) => this.showUsersCompany(element, true)
            }
          ]
        }
      ];
    }else{
      this.grantedColumns = [
        {key: "Email", display: this.translate.instant("GENERIC_FIELDS.EMAIL")},
        {
          key: "action",
          display: "",
          type: TypeColumn.SimpleAction,
          styles: {flex: "0 0 8%"},
          actions: [
            {
              icon: "delete",
              name: "",
              fn : (element) => this.removeAuthorization(element),
              tooltip: {tooltipText: this.translate.instant("GENERIC_LABEL.DELETE_ROW"), tooltipPosition: TooltipPositionType.Above}
            }
          ]
        }
      ]
    }
  }

  configureGrantingColumns(forCompanies : boolean = true){
    if(forCompanies){
      this.grantingColumns = [
        { key: "Denomination", display: this.translate.instant("GENERIC_FIELDS.DENOMINATION") },
        { key: "Name", display: this.translate.instant("GENERIC_FIELDS.NAME") },
        { key: "Surname", display: this.translate.instant("GENERIC_FIELDS.SURNAME") },
        { key: "VatNumber", display: this.translate.instant("GENERIC_FIELDS.VAT") },
        {
          key: "action",
          display: "",
          type: TypeColumn.SimpleAction,
          styles: {flex: "0 0 10%"},
          actions: [
            {
              icon: "zoom_in",
              name: "",
              fn : (element) => this.showUsersCompany(element, false)
            }
          ]
        }
      ]
    }else{
      this.grantingColumns = [
        {key: "Email", display: this.translate.instant("GENERIC_FIELDS.EMAIL")}
      ]
    }
  }

  async loadCompanies(){
    this.listCompaniesGranted = await this.userImpersonationService.getCompaniesByUserGranted(this.authService.getCurrentUser().ID)
    this.listCompaniesGranting = await this.userImpersonationService.getCompaniesByUserGranting(this.authService.getCurrentUser().ID)
  }

  async showUsersCompany(element : CompanyListDTO, isGranted : boolean){
    let dto : AuthorizationDTO = new AuthorizationDTO();
    dto.FK_user = this.authService.getCurrentUser().ID.toString();
    dto.FK_Company = element.ID
    if(isGranted){
      this.nameGrantedCompanyToShow = element.Denomination;
      this.showCompaniesGranted = false;
      this.configureGrantedColumns(false);
      this.usersGranted = await this.userImpersonationService.getUsersGranted(dto)
    }
    else{
      this.nameGrantingCompanyToShow = element.Denomination;
      this.showCompaniesGranting = false;
      this.configureGrantingColumns(false);
      this.usersGranting = await this.userImpersonationService.getUsersGranting(dto)
    }
  }

  openModalAddAuthorization(){
    this.authForm = this.formBuilder.group({
      email : [this.authEmail, Validators.pattern(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]
    })

    this.modalAddAuthorizationRef = this.dialog.open(this.modalAddAuthorization, {
      disableClose: false,
      hasBackdrop: true,
      minHeight: '30%',
      minWidth: '40%'
    })
  }

  closeModal(){
    this.modalAddAuthorizationRef.close();
    this.authEmail = "";
  }

  saveNewAuthorization(){
    this.showCompaniesGranted = true;
    let dto : AuthorizationDTO = new AuthorizationDTO();
    dto.FK_user = this.authService.getCurrentUser().ID.toString();
    dto.Email = this.authEmail;
    this.userImpersonationService.addAuthorization(dto).then(() => {
      this.closeModal();
      this.loadCompanies();
      dto = new AuthorizationDTO();
    }).catch(error => {
      DialogService.Error(error.message);
    })
  }

  removeAuthorization(element : UserListDTO){
    let dto : AuthorizationDTO = new AuthorizationDTO();
    dto.FK_user = this.authService.getCurrentUser().ID.toString();
    dto.Email = element.Email;

    DialogService.Confirm(this.translate.instant("DIALOGS.DELETE_CONFIRM"), () => {
      this.userImpersonationService.removeAuthorization(dto).then(() => {
        DialogService.Success(this.translate.instant("DIALOGS.DELETE_SUCCESS"));
        this.showCompaniesGranted = true;
        this.configureGrantedColumns(true);
        this.loadCompanies();
      }).catch(error => {
        DialogService.Error(error.message);
      });
    }, true);
  }

}
